import useAppLanguage from '../i18n';
import SingleLineText from './ui/SingleLineText';
import React from 'react';
import { Box } from '@mui/material';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import MedicationRoundedIcon from '@mui/icons-material/MedicationRounded';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import EditNoteIcon from '@mui/icons-material/EditNote';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const PRESET = {
    A: {
        bg: '#fffaeb', bgEnd: '#fff5d6', border: '1px solid #fde68a',
        blob: '#fde68a',
        tileFrom: '#fde68a', tileTo: '#fbbf24',
        title: '#5a4a00', sub: '#8a7548',
        chevron: '#b59b48', iconOnTile: '#7c2d12',
    },
    B: {
        bg: '#fef2f2', bgEnd: '#fee2e2', border: '1px solid #fecaca',
        blob: '#fecaca',
        tileFrom: '#fecaca', tileTo: '#f87171',
        title: '#b91c1c', sub: '#991b1b',
        chevron: '#dc2626', iconOnTile: '#7f1d1d',
    },
    C: {
        bg: '#f0f9ff', bgEnd: '#e0f2fe', border: '1px solid #bae6fd',
        blob: '#bae6fd',
        tileFrom: '#bae6fd', tileTo: '#38bdf8',
        title: '#0c4a6e', sub: '#3f6c8a',
        chevron: '#7aa3c4', iconOnTile: '#0c4a6e',
    },
    D: {
        bg: '#faf5ff', bgEnd: '#f3e8ff', border: '1px solid #e9d5ff',
        blob: '#e9d5ff',
        tileFrom: '#d8b4fe', tileTo: '#9333ea',
        title: '#581c87', sub: '#6b46a3',
        chevron: '#9333ea', iconOnTile: '#3b0764',
    },
    E: {
        bg: '#fff7ed', bgEnd: '#ffedd5', border: '1px solid #fed7aa',
        blob: '#fed7aa',
        tileFrom: '#fdba74', tileTo: '#f97316',
        title: '#7c2d12', sub: '#9a3412',
        chevron: '#ea580c', iconOnTile: '#7c2d12',
    },
    F: {
        bg: '#f0fdf4', bgEnd: '#dcfce7', border: '1px solid #bbf7d0',
        blob: '#bbf7d0',
        tileFrom: '#86efac', tileTo: '#22c55e',
        title: '#14532d', sub: '#3f6c4f',
        chevron: '#16a34a', iconOnTile: '#14532d',
    },
    G: {
        bg: '#f8fafc', bgEnd: '#f1f5f9', border: '1px solid #e2e8f0',
        blob: '#e2e8f0',
        tileFrom: '#cbd5e1', tileTo: '#64748b',
        title: '#1e293b', sub: '#475569',
        chevron: '#64748b', iconOnTile: '#1e293b',
    },
};

const ICON_BY_STATE = {
    A: FamilyRestroomIcon,
    B: WarningAmberIcon,
    C: MedicationRoundedIcon,
    D: SmartToyIcon,
    E: Inventory2Icon,
    F: EditNoteIcon,
    G: RateReviewIcon,
};

PRESET.H = { bg: '#f0fdfa', bgEnd: '#ccfbf1', border: '1px solid #99f6e4', blob: '#99f6e4', tileFrom: '#5eead4', tileTo: '#14b8a6', title: '#134e4a', sub: '#326b65', chevron: '#0f766e', iconOnTile: '#134e4a' };
PRESET.I = PRESET.C;
ICON_BY_STATE.H = BlockRoundedIcon;
ICON_BY_STATE.I = TouchAppIcon;
const VALID_STATES = new Set(['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I']);

export default function FamilyHomeCard({ state, primary, extraCount = 0, connected = false, connectedSubtitle, hasUnreadReviewReply = false, onClick }) {
    const { language, t } = useAppLanguage();
    if (!VALID_STATES.has(state)) return null;

    const preset = PRESET[state];
    const Icon = ICON_BY_STATE[state];

    let title;
    let subtitle;
    if (state === 'H') {
        title = t('광고 없이 편하게 사용해요');
        subtitle = t('약 챙기는 일에만 집중해요');
    } else if (state === 'I') {
        title = t('첫 약을 등록해 보세요');
        subtitle = t('먹을 시간부터 다시 알림까지 챙겨요');
    } else if (state === 'B') {
        const nickname = primary?.nickname || t('가족');
        title = t('{nickname}님이 약을 안 드셨어요', { nickname });
        subtitle = extraCount > 0 ? t('외 {count}명도 확인해주세요', { count: extraCount }) : t('지금 확인해주세요');
    } else if (state === 'C') {
        title = t('필요할 때 먹는 약도 기록해요');
        subtitle = t('진통제·소화제, 언제 먹었는지 확인해요');
    } else if (state === 'D') {
        title = t('약 궁금증, AI에게 물어보세요');
        subtitle = t('이 약과 안 맞는 음식이 뭘까요?');
    } else if (state === 'E') {
        title = t('약이 떨어지기 전에 알려드려요');
        subtitle = t('약국 갈 타이밍 놓치지 마세요');
    } else if (state === 'F') {
        title = t('증상 기록해두기');
        subtitle = t('두통, 속쓰림 같은 변화를 남겨요');
    } else if (state === 'G') {
        title = t(hasUnreadReviewReply ? '건의 답변이 도착했어요' : '앱에 필요한 기능이 있나요?');
        subtitle = t(hasUnreadReviewReply ? '개발자 답변을 확인해보세요' : '웬만하면 반영돼요');
    } else if (connected) {
        title = t('가족 약 챙기는 중');
        subtitle = connectedSubtitle || t('오늘 누가 약을 잘 챙겼는지 보기');
    } else {
        title = t('엄마 아빠 약, 같이 챙겨요');
        subtitle = t('안 드시면 알림으로 알려드려요');
    }

    return (
        <Box
            onClick={onClick}
            onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onClick?.(); } }}
            role="button"
            tabIndex={0}
            data-tappable="true"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '14px',
                cursor: 'pointer',
                background: `linear-gradient(135deg, ${preset.bg} 0%, ${preset.bgEnd} 100%)`,
                border: preset.border,
                padding: '10px 12px 10px 14px',
                minHeight: 76,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                transition: 'opacity 0.15s ease, transform 0.15s ease',
                '&:active': { opacity: 0.7, transform: 'scale(0.99)' },
            }}
        >
            <Box sx={{
                position: 'absolute', right: -20, top: -30,
                width: 90, height: 90, borderRadius: '50%',
                bgcolor: preset.blob, opacity: 0.4,
                pointerEvents: 'none',
            }} />
            <Box sx={{
                position: 'absolute', right: 40, bottom: -40,
                width: 70, height: 70, borderRadius: '50%',
                bgcolor: preset.blob, opacity: 0.25,
                pointerEvents: 'none',
            }} />

            <Box sx={{
                display: 'flex', alignItems: 'center', gap: '8px',
                position: 'relative', zIndex: 1, width: '100%',
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <SingleLineText sx={{
                        fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em',
                        color: preset.title, lineHeight: 1.3, mb: '3px',
                        ...(language === 'ko' ? {} : { whiteSpace: 'normal', overflowWrap: 'anywhere' }),
                        wordBreak: 'keep-all',
                    }}>
                        {title}
                    </SingleLineText>
                    <SingleLineText sx={{
                        fontSize: 13, fontWeight: 500, letterSpacing: '-0.01em',
                        color: preset.sub, lineHeight: 1.45,
                        ...(language === 'ko' ? {} : { whiteSpace: 'normal', overflowWrap: 'anywhere' }),
                    }}>
                        {subtitle}
                    </SingleLineText>
                </Box>

                <Box sx={{
                    width: 48, height: 48, borderRadius: '12px', flexShrink: 0,
                    background: `linear-gradient(135deg, ${preset.tileFrom}, ${preset.tileTo})`,
                    boxShadow: `0 6px 14px -6px ${preset.tileTo}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative',
                }}>
                    <Icon sx={{ fontSize: 30, color: preset.iconOnTile }} />
                    {state === 'G' && hasUnreadReviewReply && (
                        <Box sx={{
                            position: 'absolute', right: -2, top: -2,
                            width: 12, height: 12, borderRadius: '50%',
                            bgcolor: '#f59e0b',
                            border: '2px solid rgba(255,255,255,0.9)',
                            boxShadow: '0 0 0 4px rgba(245,158,11,0.16)',
                            pointerEvents: 'none',
                        }} />
                    )}
                </Box>

                <ChevronRightIcon sx={{
                    fontSize: 18, color: preset.chevron, opacity: 0.7, flexShrink: 0,
                }} />
            </Box>
        </Box>
    );
}
